/**
 * ListTreeEditor — shared tree editor for templates and checklists.
 *
 * Three modes, driven by ChecklistContext:
 * - **edit**: DnD reordering, inline title editing, add/remove, context menus
 * - **view**: collapsible groups, disabled controls, click → drill-down
 * - **fill-in**: collapsible groups, active controls, click → drill-down
 *
 * Edit mode uses @dnd-kit/react v0.4 with type/accept/group constraints
 * (proven in the DnD POC). View/fill-in mode delegates to the existing
 * Field component hierarchy.
 */

import { Badge } from '@/components/ui/Badge/badge';
import { Button } from '@/components/ui/button';
import { EditableText } from '@/components/ui/EditableText';
import { cn } from '@/lib/utils';
import { CheckCircle, Loader2, Plus } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  DndRowMenu,
  DragHandle,
  DrillDownButton,
} from '@/features/lists/components/dnd-row-parts';

// DnD
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { CollisionPriority } from '@dnd-kit/abstract';
import { move } from '@dnd-kit/helpers';

// Domain
import {
  createListEvent,
  type Field as FieldModel,
  type GroupField as GroupFieldModel,
  type RootField,
  type ValueField,
} from '@certhon/models';
import { useListContext } from '@/features/lists/context';
import { createPartialField } from '@/features/lists/components/field-factory';
import { CheckpointControl } from '@/features/lists/components/CheckpointControl';
import { NumericControl } from '@/features/lists/components/NumericControl';
import { TaskControl } from '@/features/lists/components/TaskControl';
import { Field } from '@/features/lists/components/Field';
import { FieldFeedbackBadge } from '@/features/feedback/components/FieldFeedbackBadge';
import {
  PreviewLocaleProvider,
  type PreviewLocale,
  usePreviewLocale,
} from '@/features/lists/components/PreviewLocaleContext';
import { AddGroupMenu } from './AddGroupMenu';
import { usePostListEventsMutation } from '@/features/lists/lists.hooks';

// =============================================================================
// Public API
// =============================================================================

/**
 * Adjust a target index for the reducer's insert-then-remove semantics.
 * When moving forward in the same parent, the insert happens before
 * the removal, so we need index + 1.
 */
function adjustIndexForInsertRemove(
  oldIndex: number,
  newIndex: number,
): number {
  return oldIndex !== -1 && oldIndex < newIndex ? newIndex + 1 : newIndex;
}

interface ListTreeEditorProps {
  tree: RootField;
  activeFieldId?: string;
  /** Selection changed (title click, arrows). */
  onSelectField?: (fieldId: string) => void;
  /** Explicit drill-down (chevron click). Always opens detail panel. */
  onDrillDown?: (fieldId: string) => void;
  locale?: PreviewLocale;
  onEnterEditMode?: () => void;
}

export function ListTreeEditor({
  tree,
  activeFieldId,
  onSelectField,
  onDrillDown,
  locale = 'nl',
  onEnterEditMode,
}: ListTreeEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mode } = useListContext();

  // Scroll active field into view on navigation
  useEffect(() => {
    if (!activeFieldId || !containerRef.current) return;
    const el = containerRef.current.querySelector(
      `#field-${CSS.escape(activeFieldId)}`,
    );
    if (el) el.scrollIntoView({ behavior: 'instant', block: 'nearest' });
  }, [activeFieldId]);

  if (mode === 'edit') {
    return (
      <PreviewLocaleProvider value={locale}>
        <div ref={containerRef} className="-mx-2 border-t md:-mx-4">
          <EditModeTree
            tree={tree}
            activeFieldId={activeFieldId}
            onSelectField={onSelectField}
            onDrillDown={onDrillDown}
          />
        </div>
      </PreviewLocaleProvider>
    );
  }

  // View / Fill-in mode — empty state
  if (tree.children.length === 0) {
    return (
      <div className="-mx-2 border-t md:-mx-4">
        <div className="text-muted-foreground py-8 text-center text-sm">
          No fields yet.{' '}
          {onEnterEditMode ? (
            <button
              type="button"
              onClick={onEnterEditMode}
              className="text-primary hover:text-primary/80 underline underline-offset-2"
            >
              Enter edit mode
            </button>
          ) : (
            'Enter edit mode'
          )}{' '}
          to add content.
        </div>
      </div>
    );
  }

  // View / Fill-in mode — delegate to existing Field component hierarchy
  return (
    <PreviewLocaleProvider value={locale}>
      <div ref={containerRef} className="-mx-2 border-t md:-mx-4">
        {tree.children.map((child, index) => (
          <Field
            key={child.id}
            field={child}
            index={index}
            activeFieldId={activeFieldId}
            onSelectField={onSelectField}
          />
        ))}
        <CompleteListFooter />
      </div>
    </PreviewLocaleProvider>
  );
}

// =============================================================================
// CompleteListFooter — surfaces the close/reopen action at the bottom of
// the tree so users don't have to dig through the entity-actions menu.
// =============================================================================

function CompleteListFooter() {
  const { list, isTemplate, user } = useListContext();
  const postEvents = usePostListEventsMutation();

  if (isTemplate) return null;

  if (list.status === 'closed') {
    return (
      <div className="text-muted-foreground border-t px-3 py-2 text-center text-xs">
        List is completed.
      </div>
    );
  }

  const handleComplete = () => {
    postEvents.mutate([
      createListEvent('CLOSE_LIST', {
        list_id: list.id,
        user_id: user.recnum,
        payload: {},
      }),
    ]);
  };

  return (
    <div className="mt-2 flex justify-center px-3 py-3">
      <Button
        variant="outline"
        size="sm"
        onClick={handleComplete}
        disabled={postEvents.isPending}
        className="gap-1"
      >
        {postEvents.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <CheckCircle className="h-3.5 w-3.5" />
        )}
        Mark as completed
      </Button>
    </div>
  );
}

// =============================================================================
// Edit mode tree with DnD (@dnd-kit/react v0.4)
// =============================================================================

interface EditModeTreeProps {
  tree: RootField;
  activeFieldId?: string;
  onSelectField?: (fieldId: string) => void;
  onDrillDown?: (fieldId: string) => void;
}

function EditModeTree({
  tree,
  activeFieldId,
  onSelectField,
  onDrillDown,
}: EditModeTreeProps) {
  const { addEvent, list, user } = useListContext();
  const listId = list.id;
  const userId = user.recnum;

  // -------------------------------------------------------------------------
  // Derive DnD-compatible state from event-sourced tree
  // -------------------------------------------------------------------------

  const derived = useMemo(() => {
    const groupOrder: string[] = [];
    const fieldOrder: Record<string, string[]> = {};
    const nodeMap = new Map<string, FieldModel>();

    for (const child of tree.children) {
      if (child.type === 'GROUP_FIELD') {
        const group = child as GroupFieldModel;
        groupOrder.push(group.id);
        nodeMap.set(group.id, group);
        fieldOrder[group.id] = group.children.map((f) => f.id);
        for (const f of group.children) {
          nodeMap.set(f.id, f);
        }
      }
    }

    return { groupOrder, fieldOrder, nodeMap };
  }, [tree]);

  // Local DnD state — synced from tree, modified during drag
  const [localGroupOrder, setLocalGroupOrder] = useState(derived.groupOrder);
  const [localFieldOrder, setLocalFieldOrder] = useState(derived.fieldOrder);

  // Refs shadow local state for reliable DnD snapshots (avoids stale closures)
  const localGroupOrderRef = useRef(localGroupOrder);
  const localFieldOrderRef = useRef(localFieldOrder);
  useEffect(() => {
    localGroupOrderRef.current = localGroupOrder;
    localFieldOrderRef.current = localFieldOrder;
  }, [localGroupOrder, localFieldOrder]);

  const previousDndState = useRef({
    groupOrder: derived.groupOrder,
    fieldOrder: derived.fieldOrder,
  });

  // Sync when tree rebuilds (after events are applied)
  useEffect(() => {
    setLocalGroupOrder(derived.groupOrder);
    setLocalFieldOrder(derived.fieldOrder);
  }, [derived.groupOrder, derived.fieldOrder]);

  // UI state for inline editing (selection is driven by URL via activeFieldId)
  // Tracks node whose title is currently being edited; only the setter is read
  // externally (callbacks in the row components manage edit state via setter).
  const [, setEditingTitleId] = useState<string | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // Select a node: update URL (single source of truth) + local editing state
  const selectNode = useCallback(
    (nodeId: string) => {
      onSelectField?.(nodeId);
    },
    [onSelectField],
  );

  // -------------------------------------------------------------------------
  // DnD handlers (POC pattern)
  // -------------------------------------------------------------------------

  const handleDragStart = useCallback(() => {
    previousDndState.current = {
      groupOrder: localGroupOrderRef.current,
      fieldOrder: localFieldOrderRef.current,
    };
  }, []);

  const handleDragOver = useCallback(
    (
      event: Parameters<
        NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragOver']>
      >[0],
    ) => {
      const { source } = event.operation;
      if (!source) return;
      if (source.type === 'group') return; // groups reorder on end only

      setLocalFieldOrder((prev) => move(prev, event));
    },
    [],
  );

  const handleDragEnd = useCallback(
    (
      event: Parameters<
        NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragEnd']>
      >[0],
    ) => {
      const { source } = event.operation;
      if (!source) return;

      if (event.canceled) {
        // Restore both on cancel — defensive against future changes
        setLocalGroupOrder(previousDndState.current.groupOrder);
        setLocalFieldOrder(previousDndState.current.fieldOrder);
        return;
      }

      if (source.type === 'group') {
        const prevGroupOrder = previousDndState.current.groupOrder;
        const newGroupOrder = move(localGroupOrder, event);
        setLocalGroupOrder(newGroupOrder);

        const sourceId = String(source.id);
        const newIndex = newGroupOrder.indexOf(sourceId);
        const oldIndex = prevGroupOrder.indexOf(sourceId);

        // Skip no-op: dropped in the same position
        if (newIndex !== -1 && newIndex !== oldIndex) {
          const adjustedIndex = adjustIndexForInsertRemove(oldIndex, newIndex);

          addEvent(
            createListEvent('MOVE', {
              list_id: listId,
              user_id: userId,
              payload: {
                targetNodeId: tree.id,
                subjectNodeId: sourceId,
                index: adjustedIndex,
              },
            }),
          );
        }
      } else {
        // Field — already moved during drag via onDragOver
        // Find new parent group and index from localFieldOrder
        const fieldId = String(source.id);
        const prevFieldOrder = previousDndState.current.fieldOrder;

        // Find original parent group
        let originalGroupId: string | null = null;
        let originalIndex = -1;
        for (const [gid, fids] of Object.entries(prevFieldOrder)) {
          const fi = fids.indexOf(fieldId);
          if (fi !== -1) {
            originalGroupId = gid;
            originalIndex = fi;
            break;
          }
        }

        for (const [groupId, fieldIds] of Object.entries(localFieldOrder)) {
          const idx = fieldIds.indexOf(fieldId);
          if (idx !== -1) {
            const sameParent = groupId === originalGroupId;

            // Skip no-op: dropped in the same position within the same group
            if (sameParent && idx === originalIndex) break;

            const adjustedIdx = sameParent
              ? adjustIndexForInsertRemove(originalIndex, idx)
              : idx;

            addEvent(
              createListEvent('MOVE', {
                list_id: listId,
                user_id: userId,
                payload: {
                  targetNodeId: groupId,
                  subjectNodeId: fieldId,
                  index: adjustedIdx,
                },
              }),
            );
            break;
          }
        }
      }
    },
    [localGroupOrder, localFieldOrder, addEvent, listId, userId, tree.id],
  );

  // -------------------------------------------------------------------------
  // Tree mutations → ListEvents
  // -------------------------------------------------------------------------

  const patchTitle = useCallback(
    (nodeId: string, title: string, titleLocale: PreviewLocale) => {
      const field = titleLocale === 'nl' ? 'title_nl' : 'title_en';
      addEvent(
        createListEvent('PATCH', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: nodeId, partialNode: { [field]: title } },
        }),
      );
    },
    [addEvent, listId, userId],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      addEvent(
        createListEvent('REMOVE', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: nodeId },
        }),
      );
    },
    [addEvent, listId, userId],
  );

  const copyNode = useCallback(
    (parentId: string, subjectId: string, index?: number) => {
      addEvent(
        createListEvent('COPY', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: parentId, subjectNodeId: subjectId, index },
        }),
      );
    },
    [addEvent, listId, userId],
  );

  const insertField = useCallback(
    (groupId: string) => {
      const partial = createPartialField('CHECKPOINT_FIELD');
      addEvent(
        createListEvent('INSERT', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: groupId, node: partial },
        }),
      );
      setPendingFocusId(partial.id);
      setEditingTitleId(partial.id);
    },
    [addEvent, listId, userId],
  );

  const insertGroup = useCallback(
    (afterGroupId?: string) => {
      const partial = createPartialField('GROUP_FIELD');
      const index = afterGroupId
        ? localGroupOrder.indexOf(afterGroupId) + 1
        : undefined;
      addEvent(
        createListEvent('INSERT', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: tree.id, node: partial, index },
        }),
      );
      setPendingFocusId(partial.id);
      setEditingTitleId(partial.id);
    },
    [addEvent, listId, userId, tree.id, localGroupOrder],
  );

  // -------------------------------------------------------------------------
  // Flat node order for arrow-key navigation
  // -------------------------------------------------------------------------

  const flatNodeIds = useMemo(() => {
    const ids: string[] = [];
    for (const gid of localGroupOrder) {
      ids.push(gid);
      for (const fid of localFieldOrder[gid] ?? []) {
        ids.push(fid);
      }
    }
    return ids;
  }, [localGroupOrder, localFieldOrder]);

  const focusNode = useCallback(
    (nodeId: string) => {
      setPendingFocusId(nodeId);
      setEditingTitleId(nodeId);
      selectNode(nodeId);
    },
    [selectNode],
  );

  const navigateVertical = useCallback(
    (currentId: string, direction: 'up' | 'down') => {
      const idx = flatNodeIds.indexOf(currentId);
      if (idx === -1) return;
      const next = direction === 'up' ? idx - 1 : idx + 1;
      if (next < 0 || next >= flatNodeIds.length) return;
      focusNode(flatNodeIds[next]);
    },
    [flatNodeIds, focusNode],
  );

  // -------------------------------------------------------------------------
  // Keyboard handler (shared for both groups and fields)
  // -------------------------------------------------------------------------

  const handleTitleKeyDown = useCallback(
    (
      e: KeyboardEvent<HTMLSpanElement>,
      nodeId: string,
      parentGroupId: string,
      currentTitle: string,
    ) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        (e.target as HTMLElement).blur();
        navigateVertical(nodeId, e.key === 'ArrowUp' ? 'up' : 'down');
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        (e.target as HTMLElement).blur();
        // Empty node + Ctrl+Enter → user wanted a group, not this node
        if (!currentTitle.trim()) {
          removeNode(nodeId);
        }
        insertGroup(parentGroupId);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        (e.target as HTMLElement).blur();
        insertField(parentGroupId);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (!currentTitle.trim()) {
          removeNode(nodeId);
        }
        setEditingTitleId(null);
        (e.target as HTMLElement).blur();
      }
    },
    [insertField, insertGroup, removeNode, navigateVertical],
  );

  // -------------------------------------------------------------------------
  // Title blur handler — commit title or remove empty node
  // -------------------------------------------------------------------------

  const handleTitleBlur = useCallback(
    (nodeId: string, newTitle: string, titleLocale: PreviewLocale) => {
      // Only patch if the title actually changed
      const node = derived.nodeMap.get(nodeId);
      if (node && 'title_nl' in node) {
        const currentValue =
          titleLocale === 'nl' ? node.title_nl : node.title_en;
        if (newTitle !== currentValue) {
          patchTitle(nodeId, newTitle, titleLocale);
        }
      }
      // Functional updates avoid stale closure over editing/focus state
      setEditingTitleId((prev) => (prev === nodeId ? null : prev));
      setPendingFocusId((prev) => (prev === nodeId ? null : prev));
    },
    [patchTitle, derived.nodeMap],
  );

  const expandTargetNodeId = tree.id;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <DragDropProvider
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {localGroupOrder.map((groupId, groupIndex) => {
        const group = derived.nodeMap.get(groupId) as
          | GroupFieldModel
          | undefined;
        if (!group) return null;
        const fieldIds = localFieldOrder[groupId] ?? [];

        return (
          <DndGroupSection
            key={groupId}
            group={group}
            groupIndex={groupIndex}
            fieldIds={fieldIds}
            nodeMap={derived.nodeMap}
            activeNodeId={activeFieldId}
            pendingFocusId={pendingFocusId}
            onSelect={selectNode}
            onDrillDown={onDrillDown}
            onEditTitle={setEditingTitleId}
            onTitleBlur={handleTitleBlur}
            onDeleteGroup={removeNode}
            onCopyGroup={(groupId) =>
              copyNode(tree.id, groupId, localGroupOrder.indexOf(groupId) + 1)
            }
            onAddField={insertField}
            onDeleteField={removeNode}
            onCopyField={(fieldId, parentGroupId, fieldIndex) =>
              copyNode(parentGroupId, fieldId, fieldIndex + 1)
            }
            onTitleKeyDown={handleTitleKeyDown}
          />
        );
      })}

      {/* Add group affordance + overflow menu (Phase 9d.2) */}
      <div className="border-t px-3 py-2">
        <AddGroupMenu
          expandTargetNodeId={expandTargetNodeId}
          onAddGroup={() => insertGroup()}
        />
      </div>
    </DragDropProvider>
  );
}

// =============================================================================
// DnD Group section
// =============================================================================

interface DndGroupSectionProps {
  group: GroupFieldModel;
  groupIndex: number;
  fieldIds: string[];
  nodeMap: Map<string, FieldModel>;
  activeNodeId?: string;
  pendingFocusId: string | null;
  onSelect: (id: string) => void;
  onDrillDown?: (id: string) => void;
  onEditTitle: (id: string | null) => void;
  onTitleBlur: (
    nodeId: string,
    newTitle: string,
    locale: PreviewLocale,
  ) => void;
  onDeleteGroup: (groupId: string) => void;
  onCopyGroup: (groupId: string) => void;
  onAddField: (groupId: string) => void;
  onDeleteField: (fieldId: string) => void;
  onCopyField: (
    fieldId: string,
    parentGroupId: string,
    fieldIndex: number,
  ) => void;
  onTitleKeyDown: (
    e: KeyboardEvent<HTMLSpanElement>,
    nodeId: string,
    parentGroupId: string,
    currentTitle: string,
  ) => void;
}

function DndGroupSection({
  group,
  groupIndex,
  fieldIds,
  nodeMap,
  activeNodeId,
  pendingFocusId,
  onSelect,
  onDrillDown,
  onEditTitle,
  onTitleBlur,
  onDeleteGroup,
  onCopyGroup,
  onAddField,
  onDeleteField,
  onCopyField,
  onTitleKeyDown,
}: DndGroupSectionProps) {
  const locale = usePreviewLocale();

  const { ref, handleRef, isDragging } = useSortable({
    id: group.id,
    index: groupIndex,
    type: 'group',
    accept: ['field', 'group'],
    collisionPriority:
      fieldIds.length === 0 ? CollisionPriority.Normal : CollisionPriority.Low,
  });

  // Edit mode: show locale-specific value WITHOUT fallback
  // so users can see which translations are missing
  const titleField = locale === 'nl' ? 'title_nl' : 'title_en';
  const localeTitle = group[titleField];
  const isTitleLocked =
    group.protoChain.length > 0 && !group.modifiedFields.includes(titleField);

  return (
    <div ref={ref} className={cn(isDragging && 'opacity-30')}>
      <DndRowMenu
        onDelete={() => onDeleteGroup(group.id)}
        deleteLabel="Delete group and fields"
        onDuplicate={() => onCopyGroup(group.id)}
      >
        <div
          id={`field-${group.id}`}
          className={cn(
            'group flex w-full items-center border-b text-left',
            'bg-muted/60 hover:bg-muted',
            activeNodeId === group.id &&
              'bg-primary/5 border-l-primary border-l-2',
          )}
        >
          <DragHandle handleRef={handleRef} ariaLabel="Drag to reorder group" />

          <div className="relative min-w-0 flex-1">
            <EditableText
              value={localeTitle}
              placeholder="Untitled group"
              ariaLabel="Group title"
              className="font-semibold tracking-tight"
              locked={isTitleLocked}
              lockedMessage="Unlock this field in the properties panel to edit."
              shouldAutoFocus={pendingFocusId === group.id}
              onFocus={() => {
                onEditTitle(group.id);
                onSelect(group.id);
              }}
              onBlur={(newTitle) => onTitleBlur(group.id, newTitle, locale)}
              onKeyDown={(e, currentTitle) =>
                onTitleKeyDown(e, group.id, group.id, currentTitle)
              }
            />
          </div>

          <FieldFeedbackBadge fieldId={group.id} size="small" />

          <Badge
            variant="secondary"
            className="mx-2 shrink-0 text-xs tabular-nums"
          >
            {fieldIds.length}
          </Badge>

          <DrillDownButton
            onClick={() => onDrillDown?.(group.id)}
            ariaLabel="Open group properties"
          />
        </div>
      </DndRowMenu>

      {/* Field rows */}
      {fieldIds.map((fieldId, fieldIndex) => {
        const field = nodeMap.get(fieldId);
        if (!field) return null;
        return (
          <DndFieldRow
            key={fieldId}
            field={field as ValueField}
            groupId={group.id}
            fieldIndex={fieldIndex}
            isActive={activeNodeId === fieldId}
            pendingFocusId={pendingFocusId}
            onSelect={onSelect}
            onDrillDown={onDrillDown}
            onEditTitle={onEditTitle}
            onTitleBlur={onTitleBlur}
            onDeleteField={onDeleteField}
            onCopyField={onCopyField}
            onTitleKeyDown={onTitleKeyDown}
          />
        );
      })}

      {/* Add field button */}
      <div className="border-b py-1 pl-8">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground gap-1 px-2 text-xs"
          onClick={() => onAddField(group.id)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add field
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// DnD Field row
// =============================================================================

interface DndFieldRowProps {
  field: ValueField;
  groupId: string;
  fieldIndex: number;
  isActive: boolean;
  pendingFocusId: string | null;
  onSelect: (id: string) => void;
  onDrillDown?: (id: string) => void;
  onEditTitle: (id: string | null) => void;
  onTitleBlur: (
    nodeId: string,
    newTitle: string,
    locale: PreviewLocale,
  ) => void;
  onDeleteField: (fieldId: string) => void;
  onCopyField: (
    fieldId: string,
    parentGroupId: string,
    fieldIndex: number,
  ) => void;
  onTitleKeyDown: (
    e: KeyboardEvent<HTMLSpanElement>,
    nodeId: string,
    parentGroupId: string,
    currentTitle: string,
  ) => void;
}

function DndFieldRow({
  field,
  groupId,
  fieldIndex,
  isActive,
  pendingFocusId,
  onSelect,
  onDrillDown,
  onEditTitle,
  onTitleBlur,
  onDeleteField,
  onCopyField,
  onTitleKeyDown,
}: DndFieldRowProps) {
  const locale = usePreviewLocale();
  const isEven = fieldIndex % 2 === 0;

  const { ref, handleRef, isDragging } = useSortable({
    id: field.id,
    index: fieldIndex,
    type: 'field',
    accept: 'field',
    group: groupId,
  });

  // Edit mode: locale-specific value WITHOUT fallback
  const titleField = locale === 'nl' ? 'title_nl' : 'title_en';
  const localeTitle = field[titleField];
  const isTitleLocked =
    field.protoChain.length > 0 && !field.modifiedFields.includes(titleField);

  return (
    <DndRowMenu
      onDelete={() => onDeleteField(field.id)}
      deleteLabel="Delete"
      onDuplicate={() => onCopyField(field.id, groupId, fieldIndex)}
    >
      <div
        ref={ref}
        id={`field-${field.id}`}
        className={cn(
          'group flex w-full items-center border-b',
          'transition-colors',
          isDragging && 'opacity-30',
          isActive && !isDragging && 'bg-primary/5 border-l-primary border-l-2',
          !isActive && !isDragging && isEven && 'bg-card',
          !isActive &&
            !isDragging &&
            !isEven &&
            'bg-foreground/[0.03] dark:bg-foreground/[0.04]',
          !isActive && !isDragging && 'hover:bg-foreground/[0.06]',
        )}
      >
        <DragHandle handleRef={handleRef} ariaLabel="Drag to reorder field" />

        <div className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-1 pl-2">
          <EditableText
            value={localeTitle}
            placeholder="Untitled field"
            ariaLabel="Field title"
            locked={isTitleLocked}
            lockedMessage="Unlock this field in the properties panel to edit."
            shouldAutoFocus={pendingFocusId === field.id}
            onFocus={() => {
              onEditTitle(field.id);
              onSelect(field.id);
            }}
            onBlur={(newTitle) => onTitleBlur(field.id, newTitle, locale)}
            onKeyDown={(e, currentTitle) =>
              onTitleKeyDown(e, field.id, groupId, currentTitle)
            }
          />
        </div>

        <FieldFeedbackBadge fieldId={field.id} size="small" />

        <div className="shrink-0 pr-1">
          <DisabledFieldControl field={field} />
        </div>

        <DrillDownButton
          onClick={() => onDrillDown?.(field.id)}
          ariaLabel="Open field properties"
        />
      </div>
    </DndRowMenu>
  );
}

// =============================================================================
// Disabled field control (shows real control at fixed width for text space)
// =============================================================================

const noop = () => {};

function DisabledFieldControl({ field }: { field: ValueField }) {
  switch (field.type) {
    case 'CHECKPOINT_FIELD':
      return (
        <CheckpointControl
          value={field.value as boolean | null}
          onChange={noop}
          disabled
        />
      );
    case 'NUMERIC_FIELD':
      return (
        <NumericControl
          value={field.value as number | null}
          onChange={noop}
          disabled
        />
      );
    case 'TASK_FIELD':
      return (
        <TaskControl
          value={field.value as boolean | null}
          onChange={noop}
          disabled
        />
      );
    default:
      return null;
  }
}
